/** Link-Vorschauen und Rich-Embeds (Bilder/Videos über die vom Server gelieferten Proxy-URLs). */
import type { EmbedDto } from '@lagerfeuer/shared';
import { useState } from 'react';
import { safeUrl, fitSize, Lightbox, type LightboxImage } from './Attachments';
import { Markdown } from './Markdown';

function colorOf(c: number | null): string | undefined {
  return c == null ? undefined : `#${(c & 0xffffff).toString(16).padStart(6, '0')}`;
}

function EmbedView({ e, serverId, onOpen }: { e: EmbedDto; serverId: string | null; onOpen: (img: LightboxImage) => void }) {
  const link = safeUrl(e.url);
  const image = e.image ? safeUrl(e.image.url) : null;
  const thumb = e.thumbnail ? safeUrl(e.thumbnail.url) : null;
  const video = e.video ? safeUrl(e.video.url) : null;

  // Reine Medien-Embeds (Bild-/GIF-Links) ohne Rahmen.
  if ((e.type === 'IMAGE' || e.type === 'GIFV') && (image || thumb || video)) {
    if (e.type === 'GIFV' && video) {
      const size = fitSize(e.video!.width, e.video!.height, 400, 320);
      return <video className="embed-media" src={video} autoPlay loop muted playsInline width={size?.width} height={size?.height} data-testid="message-embed" data-type={e.type} />;
    }
    const src = (image ?? thumb)!;
    const dims = e.image ?? e.thumbnail;
    const size = fitSize(dims?.width ?? null, dims?.height ?? null, 400, 320);
    return (
      <button type="button" className="embed-media attachment-image" onClick={() => onOpen({ url: src, alt: e.title ?? '', original: link ?? src })} data-testid="message-embed" data-type={e.type} aria-label="Bild vergrößern">
        <img src={src} alt={e.title ?? ''} width={size?.width} height={size?.height} loading="lazy" />
      </button>
    );
  }

  const authorIcon = e.author ? safeUrl(e.author.iconUrl) : null;
  const authorUrl = e.author ? safeUrl(e.author.url) : null;
  const footerIcon = e.footer ? safeUrl(e.footer.iconUrl) : null;
  return (
    <article className="embed" style={{ '--lf-embed-color': colorOf(e.color) } as React.CSSProperties} data-testid="message-embed" data-type={e.type}>
      <div className="embed-body">
        {e.siteName && <div className="embed-site faint small">{e.siteName}</div>}
        {e.author && (
          <div className="embed-author">
            {authorIcon && <img src={authorIcon} alt="" width={20} height={20} />}
            {authorUrl ? (
              <a href={authorUrl} target="_blank" rel="noopener noreferrer nofollow">
                {e.author.name}
              </a>
            ) : (
              <span>{e.author.name}</span>
            )}
          </div>
        )}
        {e.title &&
          (link ? (
            <a className="embed-title" href={link} target="_blank" rel="noopener noreferrer nofollow" data-testid="embed-title">
              {e.title}
            </a>
          ) : (
            <div className="embed-title" data-testid="embed-title">
              {e.title}
            </div>
          ))}
        {e.description && <Markdown className="embed-description" content={e.description} serverId={serverId} />}
        {e.fields.length > 0 && (
          <div className="embed-fields">
            {e.fields.map((f, i) => (
              <div key={i} className={`embed-field ${f.inline ? 'inline' : ''}`}>
                <div className="embed-field-name">{f.name}</div>
                <Markdown className="embed-field-value" content={f.value} serverId={serverId} />
              </div>
            ))}
          </div>
        )}
        {e.type === 'VIDEO' && video ? (
          <video className="embed-video" src={video} poster={thumb ?? image ?? undefined} controls preload="none" />
        ) : (
          image && (
            <button type="button" className="embed-image attachment-image" onClick={() => onOpen({ url: image, alt: e.title ?? '', original: link ?? image })} aria-label="Bild vergrößern">
              <img src={image} alt="" loading="lazy" />
            </button>
          )
        )}
        {(e.footer || e.timestamp) && (
          <div className="embed-footer faint small">
            {footerIcon && <img src={footerIcon} alt="" width={16} height={16} />}
            {e.footer?.text}
            {e.footer && e.timestamp && ' • '}
            {e.timestamp && new Date(e.timestamp).toLocaleString('de-DE')}
          </div>
        )}
      </div>
      {thumb && !image && e.type !== 'VIDEO' && <img className="embed-thumb" src={thumb} alt="" loading="lazy" />}
    </article>
  );
}

export function Embeds({ embeds, serverId }: { embeds: EmbedDto[]; serverId: string | null }) {
  const [open, setOpen] = useState<LightboxImage | null>(null);
  if (!embeds.length) return null;
  return (
    <div className="embeds">
      {embeds.map((e, i) => (
        <EmbedView key={`${e.url ?? ''}-${i}`} e={e} serverId={serverId} onOpen={setOpen} />
      ))}
      {open && <Lightbox image={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
